import React from 'react';

const TIME_PATTERN = /^(\d{1,2}):(\d{2})$/;

export function slotsFromDeliveryTime(raw) {
  const text = String(raw || '').trim();
  const empty = [
    { time: '', period: 'AM' },
    { time: '', period: 'PM' },
  ];
  if (!text) return { slots: empty, legacy: '' };

  const found = [];
  const pattern = /(\d{1,2}):(\d{2})\s*(AM|PM)/gi;
  let match = pattern.exec(text);
  while (match && found.length < 2) {
    const hour = Number(match[1]);
    if (hour >= 1 && hour <= 12) {
      found.push({
        time: `${hour}:${match[2]}`,
        period: match[3].toUpperCase(),
      });
    }
    match = pattern.exec(text);
  }
  if (!found.length) return { slots: empty, legacy: text };
  return {
    slots: [
      found[0],
      found[1] || { time: '', period: 'PM' },
    ],
    legacy: '',
  };
}

export function composeDeliveryTime(slot1, slot2, legacy) {
  const parts = [];
  for (const slot of [slot1, slot2]) {
    const time = String(slot?.time || '').trim();
    if (!time) continue;
    const match = time.match(TIME_PATTERN);
    if (!match) {
      return { error: 'Enter each office time as HH:MM, for example 10:30' };
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) {
      return { error: 'Office time must be between 1:00 and 12:59' };
    }
    const period = String(slot.period || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';
    parts.push(`${hour}:${String(minute).padStart(2, '0')} ${period}`);
  }
  if (!parts.length) return { value: String(legacy || '').trim() };
  const value = parts.join(', ');
  if (value.length > 80) {
    return { error: 'Office hours must be 80 characters or less' };
  }
  return { value };
}

function TimeSlot({ label, time, period, disabled, readOnly, onTime, onPeriod }) {
  return (
    <div>
      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={time}
          onChange={(event) => onTime(event.target.value.replace(/[^\d:]/g, '').slice(0, 5))}
          placeholder="10:30"
          maxLength={5}
          readOnly={readOnly}
          disabled={disabled}
          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl text-sm sm:text-base ${
            readOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''
          }`}
        />
        <select
          value={period === 'PM' ? 'PM' : 'AM'}
          onChange={(event) => onPeriod(event.target.value)}
          disabled={disabled || readOnly}
          className={`px-3 py-2 border border-gray-300 rounded-xl text-sm sm:text-base ${
            readOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : 'bg-white'
          }`}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

const HubDeliverySettings = ({
  deliveryTime1,
  period1,
  deliveryTime2,
  period2,
  legacyText,
  detailsRequired,
  disabled,
  readOnly,
  onChange,
}) => (
  <div className="col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
    <label className="flex items-start gap-2 text-sm text-gray-800">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={detailsRequired !== false}
        disabled={disabled || readOnly}
        onChange={(event) => onChange?.({ delivery_details_required: event.target.checked })}
      />
      <span>
        <span className="font-medium">Delivery details required</span>
        <span className="block text-[11px] text-gray-500 leading-relaxed mt-0.5">
          When on, agents at this hub must record receiver name, contact, delivery photo, and government ID before marking a shipment Delivered.
        </span>
      </span>
    </label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <TimeSlot
        label="Office opening time"
        time={deliveryTime1}
        period={period1 || 'AM'}
        disabled={disabled}
        readOnly={readOnly}
        onTime={(value) => onChange?.({ delivery_time_1: value })}
        onPeriod={(value) => onChange?.({ delivery_period_1: value })}
      />
      <TimeSlot
        label="Office closing time"
        time={deliveryTime2}
        period={period2 || 'PM'}
        disabled={disabled}
        readOnly={readOnly}
        onTime={(value) => onChange?.({ delivery_time_2: value })}
        onPeriod={(value) => onChange?.({ delivery_period_2: value })}
      />
    </div>
    <p className="mt-2 text-[11px] text-gray-400">
      Opening and closing are optional. Each one needs a time and AM or PM. They print as office hours on the shipment receipt and invoice.
    </p>
    {legacyText ? (
      <p className="mt-1 text-[11px] text-gray-500">Saved text kept until you enter a time: {legacyText}</p>
    ) : null}
  </div>
);

export default HubDeliverySettings;
